// Web MIDI live control - built around the M-Audio (Keystation/Oxygen) workflow from NEU.pde,
// but considerably expanded for live VJ use:
//
//   * Continuous controllers (faders/knobs) drive named GLOBAL parameters - master intensity,
//     particle size, hue rotation, bloom, speed and audio gain - not just the last-toggled effect.
//   * MIDI Learn: arm a parameter, wiggle any knob, and it is bound to that CC from then on.
//     Bindings persist in localStorage so a rig stays configured between sessions.
//   * Note messages trigger effects, so the keyboard's actual keys become an effect launcher
//     (notes toggle effects, the two lowest notes fire the one-shot bursts).
//
// Entirely optional/best-effort: with no Web MIDI support or no device connected this simply
// stays inactive and the piece runs fine on keyboard/on-screen controls alone.

import { state, toggleEffect } from "./state.js";

const STORAGE_KEY = "starcode.midiBindings.v1";

// Named, MIDI-assignable global parameters. `apply` maps the raw 0..1 controller value into the
// parameter's useful range - deliberately not linear everywhere (e.g. speed and size feel better
// with their neutral value near the middle of the fader's travel).
export const MIDI_TARGETS = {
  master: {
    label: "Master-Intensität",
    apply: (v) => (state.masterIntensity = v * 2),
  },
  size: {
    label: "Partikelgröße",
    apply: (v) => (state.sizeBoost = 0.25 + v * 2.75),
  },
  hue: {
    label: "Farbton",
    apply: (v) => (state.hueShift = v),
  },
  bloom: {
    label: "Bloom/Glow",
    apply: (v) => (state.bloomAmount = v * 3),
  },
  speed: {
    label: "Geschwindigkeit",
    apply: (v) => (state.speedScale = 0.05 + v * 1.95),
  },
  rotation: {
    label: "Kamera-Rotation",
    apply: (v) => (state.rotationSpeed = v * 3),
  },
  gain: {
    label: "Audio-Gain",
    apply: (v) => (state.audioGain = v * 3),
  },
  bio: {
    label: "Bio-Licht",
    apply: (v) => (state.bioIntensity = v * 2),
  },
  focus: {
    label: "Fokus-Distanz",
    apply: (v) => (state.focusDistance = 200 + v * 1400),
  },
  dof: {
    label: "Bokeh / DOF",
    apply: (v) => (state.dofBlur = v * 2),
  },
  selected: {
    label: "Aktiver Effekt",
    apply: (v) => (state.midiValue = v),
  },
};

// Sensible defaults for a stock M-Audio controller: CC1 = mod wheel, CC7 = the main volume
// fader. Everything else is expected to be assigned via MIDI Learn.
const DEFAULT_BINDINGS = {
  1: "master",
  7: "selected",
};

// Note number -> effect. Covers roughly the lowest two octaves so a small controller can launch
// effects from its keys while the knobs stay free for intensity work.
const NOTE_EFFECTS = {
  36: "bioluminescenceOn",
  38: "spectrumColorOn",
  40: "chromaWaveOn",
  41: "beatFlashOn",
  43: "audioSizeOn",
  45: "beatPulseOn",
  47: "breathOn",
  48: "tunnelOn",
  50: "helixOn",
  52: "strobeFieldOn",
  53: "vortexOn",
  55: "galaxySpiralOn",
  57: "galaxyDiscOn",
  59: "wrapModeOn",
  60: "autoMixOn",
};

export class MidiManager {
  constructor({ particleSystem } = {}) {
    this.connected = false;
    this.particleSystem = particleSystem;
    this.deviceNames = [];

    // { [ccNumber]: targetKey }
    this.bindings = { ...DEFAULT_BINDINGS, ...this._loadBindings() };

    // When set to a target key, the next CC message binds that CC to the target instead of
    // applying it - this is the MIDI Learn mechanism.
    this.learnTarget = null;
    this.lastCC = null;
    this.onChange = null; // optional callback so the UI can refresh after a learn/bind
  }

  async start() {
    if (!navigator.requestMIDIAccess) {
      console.info("[STARCODE] Web MIDI wird von diesem Browser nicht unterstuetzt.");
      return;
    }
    try {
      const access = await navigator.requestMIDIAccess();
      const bind = (input) => {
        input.onmidimessage = (msg) => this._onMessage(msg);
        if (input.name && !this.deviceNames.includes(input.name)) this.deviceNames.push(input.name);
        this.connected = true;
        state.midiConnected = true;
      };
      for (const input of access.inputs.values()) bind(input);
      access.onstatechange = (e) => {
        if (e.port.type === "input" && e.port.state === "connected") bind(e.port);
      };
    } catch (err) {
      console.warn("[STARCODE] MIDI-Zugriff verweigert/fehlgeschlagen:", err.message);
    }
  }

  // Arm MIDI Learn for a given target key (see MIDI_TARGETS). Pass null to cancel.
  armLearn(targetKey) {
    this.learnTarget = targetKey;
    this.onChange?.();
  }

  // Which CC (if any) currently drives a target - used by the UI to show the assignment.
  ccForTarget(targetKey) {
    const found = Object.entries(this.bindings).find(([, t]) => t === targetKey);
    return found ? Number(found[0]) : null;
  }

  _onMessage(msg) {
    const [status, data1, data2] = msg.data;
    const type = status & 0xf0;

    if (type === 0xb0) {
      this.lastCC = data1;
      const value = data2 / 127;

      if (this.learnTarget) {
        // Drop any previous CC pointing at this target so a target only ever has one control.
        for (const cc of Object.keys(this.bindings)) {
          if (this.bindings[cc] === this.learnTarget) delete this.bindings[cc];
        }
        this.bindings[data1] = this.learnTarget;
        this._saveBindings();
        this.learnTarget = null;
        this.onChange?.();
        return;
      }

      const target = this.bindings[data1];
      if (target && MIDI_TARGETS[target]) {
        MIDI_TARGETS[target].apply(value);
      }
      return;
    }

    // Note On (a Note On with velocity 0 is a Note Off in disguise, so require a real velocity).
    if (type === 0x90 && data2 > 0) {
      if (data1 === 24) {
        this.particleSystem?.triggerExplosion(90);
        return;
      }
      if (data1 === 26) {
        this.particleSystem?.triggerRbBurst(90);
        return;
      }
      const effect = NOTE_EFFECTS[data1];
      if (effect) toggleEffect(effect);
    }
  }

  _loadBindings() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  _saveBindings() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.bindings));
    } catch {
      // Storage can be unavailable (private mode / disabled cookies) - bindings then simply
      // last for the current session only, which is not worth interrupting a live show for.
    }
  }
}
